import booleanCrosses from '@turf/boolean-crosses';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { Feature, lineString, Point, point } from '@turf/helpers';

import {
  convertFormCoordinatesToGeometryCoordinates,
  FormStartAndEndCoordinates,
} from 'modules/form';
import { Coordinate, RouteWaypointCoordinates } from 'modules/route/types';
import { InclusionZone } from '../types';

type Waypoint = RouteWaypointCoordinates | Feature<Point>;

const isGate = (waypoint: Waypoint): waypoint is Coordinate[] =>
  Array.isArray(waypoint) && Array.isArray(waypoint[0]);

// A point is inside the inclusion zone when it falls within the polygon. A gate
// is turned into a line between its endpoints and counts as inside when that
// line crosses the inclusion zone.
const isWaypointInInclusionZone = (
  waypoint: Waypoint,
  zone: InclusionZone['inclusionZone'],
): boolean =>
  isGate(waypoint)
    ? booleanCrosses(lineString(waypoint), zone)
    : booleanPointInPolygon(waypoint, zone);

export const useCheckCoordinatesInclusionInInclusionZone = (
  zones: InclusionZone[],
): {
  validateStartAndEndCoordinatesWithInclusionZones: (
    inclusionZoneId: number | undefined,
    coordinates: FormStartAndEndCoordinates & { stayInInclusionZone: boolean },
  ) => undefined | string;
} => {
  const validateStartAndEndCoordinatesWithInclusionZones = (
    inclusionZoneId: number | undefined,
    data: FormStartAndEndCoordinates & { stayInInclusionZone: boolean },
  ): undefined | string => {
    const relevantZone = zones.find(({ id }) => id === inclusionZoneId);

    if (!data.stayInInclusionZone || !relevantZone) {
      return undefined;
    }

    const pointCoordinates = convertFormCoordinatesToGeometryCoordinates(data);

    if (!data.route) {
      const points = [point(pointCoordinates[0]), point(pointCoordinates[1])];
      return points.some(
        (waypoint) =>
          !isWaypointInInclusionZone(waypoint, relevantZone.inclusionZone),
      )
        ? 'Routing start or end point are not within the selected inclusion zone.\r\nPlease select another inclusion zone or uncheck this checkbox to continue.'
        : undefined;
    }

    return data.route.some(
      (waypoint) =>
        !isWaypointInInclusionZone(waypoint, relevantZone.inclusionZone),
    )
      ? 'One or more routing waypoints are not within the selected inclusion zone.\r\nPlease select another inclusion zone or uncheck this checkbox to continue.'
      : undefined;
  };

  return { validateStartAndEndCoordinatesWithInclusionZones };
};
